import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { forwardRef } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import popoverStyles from './Popover.styles.esm.js';
import { usePopover } from './usePopover.esm.js';
import { cn } from '../../utils/cn.esm.js';

const Popover = forwardRef((_a, ref) => {
    var _b;
    var { children, trigger, className, inverse = false, isOpen, triggerTabIndex, onOpenAutoFocus, onOpenChange, sideOffset, hasAsChild = true, isFocusOnTrigger = false, focusTrap = false } = _a, props = __rest(_a, ["children", "trigger", "className", "inverse", "isOpen", "triggerTabIndex", "onOpenAutoFocus", "onOpenChange", "sideOffset", "hasAsChild", "isFocusOnTrigger", "focusTrap"]);
    const classes = popoverStyles({ inverse, class: className });
    const { contentId, contentRef, triggerRef, actualOpen, handleOpenAutoFocus, handleKeyDownTrigger, handleKeyDownContent, handleFocusOutside, handlePointerDownOutside, handleTriggerShiftTab, handleEscapeKeyDown, handleOpenChange, handleTriggerClick, } = usePopover({
        isOpen,
        onOpenChange,
        isFocusOnTrigger,
        focusTrap,
    });
    const enhancedTrigger = isFocusOnTrigger && React__default.isValidElement(trigger)
        ? React__default.cloneElement(trigger, Object.assign(Object.assign({}, trigger.props), { ref: triggerRef, "aria-haspopup": "dialog", "aria-expanded": actualOpen, "aria-controls": actualOpen ? contentId : undefined, role: "combobox", autoComplete: "off", onFocus: (e) => {
                var _a, _b;
                (_b = (_a = trigger.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, e);
                handleTriggerClick();
            }, onKeyDown: (e) => {
                var _a, _b;
                (_b = (_a = trigger.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e);
                handleTriggerShiftTab(e);
            } }))
        : trigger;
    return (jsxs(RadixPopover.Root, { open: actualOpen, onOpenChange: handleOpenChange, children: [isFocusOnTrigger ? (
            // When isFocusOnTrigger is true, render trigger outside of Radix control
            jsx(RadixPopover.Anchor, { asChild: true, children: jsx("div", { ref: triggerRef, className: "iris:inline-block iris:h-fit", onKeyDown: (e) => {
                        handleTriggerShiftTab(e);
                        handleEscapeKeyDown(e);
                    }, children: enhancedTrigger }) })) : (
            // Normal Radix trigger behavior
            jsx(RadixPopover.Trigger, { style: {
                    width: ((_b = props === null || props === void 0 ? void 0 : props.style) === null || _b === void 0 ? void 0 : _b.width) || undefined,
                }, tabIndex: trigger ? 0 : -1, asChild: hasAsChild, "aria-haspopup": "dialog", "aria-expanded": actualOpen, "aria-controls": actualOpen ? contentId : undefined, onKeyDown: (e) => {
                    handleKeyDownTrigger(e);
                    handleTriggerShiftTab(e);
                }, children: trigger })), jsx(RadixPopover.Content, Object.assign({ id: contentId, ref: contentRef, className: cn(classes.content(), className), sideOffset: sideOffset ? sideOffset : hasAsChild ? 8 : -8, onOpenAutoFocus: onOpenAutoFocus || handleOpenAutoFocus, onKeyDown: handleKeyDownContent, onFocusOutside: handleFocusOutside, onPointerDownOutside: handlePointerDownOutside, onEscapeKeyDown: handleEscapeKeyDown, role: "dialog" }, props, { children: children }))] }));
});
Popover.displayName = "Popover";

export { Popover };
